// The bell: push on this device and which kinds to get. No history list.
// Spec: docs/specs/push.md.

import { useEffect, useState } from 'react';
import { NOTICE_KINDS, sendTestNotice, setNoticePref, useNoticePrefs } from '../lib/data';
import { isIOS, pushState, turnPushOff, turnPushOn, type PushState } from '../lib/push';
import { CloseX, useToast } from '../ui/kit';

export function NotificationsDrawer() {
  return (
    <>
      <div className="dhead">
        <div>
          <h2 id="dtitle">Notifications</h2>
          <div className="sm">Push on this device and what to tell you</div>
        </div>
        <CloseX />
      </div>
      <div className="dbody">
        <Device />
        <Prefs />
      </div>
    </>
  );
}

const STATE_TEXT: Record<PushState, string> = {
  on: 'Push is on for this device.',
  off: 'Push is off for this device.',
  denied: 'This browser blocks notifications from the desk. Allow them in its site settings.',
  unsupported: 'This browser cannot show push notifications.',
  'needs-install':
    'On iPhone: tap Share, then "Add to Home Screen", open Ouradent from the home screen and turn push on there.',
};

function Device() {
  const toast = useToast();
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    void pushState().then(setState);
  }, []);

  async function run(fn: () => Promise<void>, done: string) {
    setBusy(true);
    try {
      await fn();
      toast(done);
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e));
    } finally {
      setState(await pushState());
      setBusy(false);
    }
  }

  return (
    <div className="blk">
      <h3>This device</h3>
      <div className="card pushcard">
        <p>{state ? STATE_TEXT[state] : 'Checking…'}</p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {state === 'off' && (
            <button
              className="btn primary"
              disabled={busy}
              onClick={() => void run(turnPushOn, 'Push is on')}
            >
              Turn on push
            </button>
          )}
          {state === 'on' && (
            <button
              className="btn ghost"
              disabled={busy}
              onClick={() => void run(turnPushOff, 'Push is off')}
            >
              Turn off
            </button>
          )}
          <button
            className="btn ghost"
            disabled={busy}
            onClick={() => void run(sendTestNotice, 'Test sent')}
          >
            Send a test
          </button>
        </div>
        {state === 'needs-install' && isIOS() && (
          <p className="hint">Works on iOS 16.4 or newer.</p>
        )}
      </div>
    </div>
  );
}

function Prefs() {
  const toast = useToast();
  const prefs = useNoticePrefs();
  const off = new Set((prefs.data ?? []).filter((p) => !p.enabled).map((p) => p.kind));
  return (
    <div className="blk">
      <h3>What to tell me</h3>
      <div className="card list-pad">
        {NOTICE_KINDS.map((k) => (
          <label key={k.id} className="orow prefrow">
            <span className="t">
              {k.label}
              <small>{k.hint}</small>
            </span>
            <input
              type="checkbox"
              checked={!off.has(k.id)}
              onChange={(e) =>
                void setNoticePref(k.id, e.target.checked).catch((err: Error) => toast(err.message))
              }
            />
          </label>
        ))}
      </div>
    </div>
  );
}
